
import React from "react";
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { Lightbulb, Palette, Code, RocketLaunch } from "@mui/icons-material";

function DevelopmentRoadmap() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const roadmapData = [
    {
      id: 1,
      title: "Book a Free Consultation",
      description: "Your journey starts here — no cost, no commitment.",
      icon: <Lightbulb />,
      steps: [
        "Schedule a consultation session with us — it’s quick and free.",
        "Explain your needs and business processes in detail so we fully understand your vision.",
      ],
      color: "#06b6d4",
      bgColor: "rgba(6, 182, 212, 0.1)",
    },
    {
      id: 2,
      title: "Requirements & Planning",
      description: "We define the solution’s scope together.",
      icon: <Palette />,
      steps: [
        "Finalize the project requirements and outline the solution.",
        "Agree on acceptance criteria, timeline, and key milestones.",
        "Set a clear budget that fits your needs and expectations.",
      ],
      color: "#06b6d4",
      bgColor: "rgba(6, 182, 212, 0.1)",
    },
    {
      id: 3,
      title: "Secure Your Spot",
      description: "Reserve our development resources with a deposit.",
      icon: <Code />,
      steps: [
        "Pay an agreed-upon deposit to kickstart the project.",
        "Your project is now locked in our schedule — development begins immediately.",
        "All transactions are secure and transparent.",
      ],
      color: "#06b6d4",
      bgColor: "rgba(6, 182, 212, 0.1)",
    },
    {
      id: 4,
      title: "Development & Updates",
      description: "We build your solution and keep you in the loop.",
      icon: <RocketLaunch />,
      steps: [
        "Our team starts building based on the agreed plan.",
        "You receive regular progress updates and previews.",
        "We adjust based on your feedback before the final delivery.",
      ],
      color: "#06b6d4",
      bgColor: "rgba(6, 182, 212, 0.1)",
    },
    {
      id: 5,
      title: "Launch & Review",
      description: "Your solution goes live — time to test it out.",
      icon: <RocketLaunch />,
      steps: [
        "We deploy your solution to a live environment.",
        "You test it thoroughly and share your feedback.",
        "We implement final tweaks to make sure it’s perfect.",
      ],
      color: "#06b6d4",
      bgColor: "rgba(6, 182, 212, 0.1)",
    },
    {
      id: 6,
      title: "Ongoing Support",
      description: "We stay with you even after launch.",
      icon: <RocketLaunch />,
      steps: [
        "Get access to post-launch support for a smooth transition.",
        "We handle maintenance, updates, and optimizations.",
        "Your success is our priority — we grow with you.",
      ],
      color: "#06b6d4",
      bgColor: "rgba(6, 182, 212, 0.1)",
    },
  ];

  return (
    <Box
      sx={{
        background:
          "linear-gradient(135deg, #0A8EF2 20%, #0f172a 100%)",
        minHeight: "100vh",
        py: { xs: 4, md: 8 },
      }}
    >
      <Container maxWidth="xl">
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 6, md: 10 }}>
          <Typography
            variant="h2"
            sx={{
              fontWeight: 500,
              mb: 2,
              fontSize: { xs: "1rem", sm: "1.5rem", md: "2.5rem" },
              color: "#FFFFFF",
            }}
          >
            The Typical Cycle at The DevHouse{" "}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: "#D1CFCF", // slate-300
              maxWidth: "600px",
              fontWeight: "300",
              mx: "auto",
              lineHeight: 1.6,
              fontSize: { xs: "0.5rem", md: "1rem" },
              px: { xs: 2, md: 0 },
            }}
          >
            Our proven development process ensures quality and client
            satisfaction at every step. From initial discovery to final launch.
          </Typography>
        </Box>

        {/* Main Roadmap */}
        <Box sx={{ maxWidth: "1200px", mx: "auto", mb: { xs: 6, md: 10 } }}>
          <Grid container spacing={{ xs: 3, md: 4 }}>
            {roadmapData.map((phase, index) => (
              <Grid item xs={12} sm={6} lg={4} key={phase.id}>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    height: "100%",
                  }}
                >
                  <Card
                    sx={{
                      background: "rgba(30, 50, 100, 0.3)", // slate-800/50
                      backdropFilter: "blur(12px)",
                      borderRadius: 4,
                      transition: "all 0.3s ease",
                      height: "100%",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: "0 10px 20px rgba(0, 0, 0, 0.5)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{ p: { xs: 3, md: 4 }, textAlign: "center" }}
                    >
                      {/* Icon */}
                      <Box
                        sx={{
                          width: 64,
                          height: 64,
                          borderRadius: "50%",
                          background: "rgba(4, 20, 0, 0.1)", 
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          mx: "auto",
                          mb: 3,
                        }}
                      >
                        {React.cloneElement(phase.icon, {
                          sx: { fontSize: 32, color: "#ffff" }, // cyan-500
                        })}
                      </Box>

                      {/* Title */}
                      <Typography
                        variant="h5"
                        sx={{
                          fontWeight: 400,
                          color: "#ffff",
                          mb: 2,
                          fontSize: { xs: "1rem", md: "1.25rem" },
                        }}
                      >
                        {phase.title}
                      </Typography>

                      {/* Description */}
                      <Typography
                        variant="body2"
                        sx={{
                          color: "#ffff", // slate-300
                          mb: 4,
                          fontSize: { xs: "0.85rem", md: ".85rem" },
                          lineHeight: 1.6,
                        }}
                      >
                        {phase.description}
                      </Typography>

                      {/* Steps */}
                      <Stack spacing={1.5} sx={{ mb: 3 }}>
                        {phase.steps.map((step, stepIndex) => (
                          <Box
                            key={stepIndex}
                            display="flex"
                            alignItems="flex-start"
                            gap={1.5}
                          >
                            <Box
                              sx={{
                                width: 6,
                                height: 6,
                                borderRadius: "50%",
                                background: "#ffff", // cyan-500
                                mt: 1,
                                flexShrink: 0,
                              }}
                            />
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#DAE1EB", // slate-400
                                fontSize: { xs: "0.8rem", md: "0.875rem" },
                                textAlign: "left",
                              }}
                            >
                              {step}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>

        {/* Timeline Stats */}
        <Paper
          elevation={0}
          sx={{
            background: "transparent",
            maxWidth: "1000px",
            mx: "auto",
          }}
        >
          {/* <Grid container spacing={{ xs: 3, md: 4 }}>
            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  background: "rgba(30, 41, 59, 0.3)", // slate-800/30
                  backdropFilter: "blur(12px)",
                  border: "1px solid #475569", // slate-600
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: "#ffff", // cyan-500
                      mb: 1,
                      fontSize: { xs: "2rem", md: "3rem" },
                    }}
                  >
                    1-4 mounth
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#ffff", // slate-300
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    }}
                  >
                    Average Project Timeline
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  background: "rgba(30, 41, 59, 0.3)", // slate-800/30
                  backdropFilter: "blur(12px)",
                  border: "1px solid #475569", // slate-600
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: "#ffff", // cyan-500
                      mb: 1,
                      fontSize: { xs: "2rem", md: "3rem" },
                    }}
                  >
                    24/7
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#cbd5e1", // slate-300
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    }}
                  >
                    Post-Launch Support
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={4}>
              <Card
                sx={{
                  background: "rgba(30, 41, 59, 0.3)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid #475569",
                  borderRadius: 3,
                  textAlign: "center",
                }}
              >
                <CardContent sx={{ p: { xs: 3, md: 4 } }}>
                  <Typography
                    variant="h3"
                    sx={{
                      fontWeight: 700,
                      color: "#ffff", // cyan-500
                      mb: 1,
                      fontSize: { xs: "2rem", md: "3rem" },
                    }}
                  >
                    100%
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: "#cbd5e1", // slate-300
                      fontSize: { xs: "0.875rem", md: "1rem" },
                    }}
                  >
                    Client Satisfaction
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid> */}
        </Paper>
      </Container>
    </Box>
  );
}

export default function Home() {
  return <DevelopmentRoadmap />;
}
